<template>
  <div ref="dropdownWrapper" class="w-full" @click.stop>
    <lf-dropdown
      v-tooltip="{
        content: $t('OFFER_CUSTOMISATION.PLEASE_SELECT_WFB_TEMPLATE'),
        onShow: () => !fundersWfbTemplateId,
      }"
      name="client-funder-template"
      menu-width="w-fit-content"
      class="w-full"
      search-enabled
      data-cy="product-dropdown"
      :value="value"
      :placeholder="placeholder ?? $t('OFFER_CUSTOMISATION.FUNDER_PRODUCT')"
      :options="options"
      :as-badge="asBadge"
      :show-placeholder="showPlaceholder"
      :fullWidth="fullWidth"
      :teleport-options="teleportOptions"
      :is-loading="isOfferTemplatesLoading"
      :disabled="disabled || !fundersWfbTemplateId"
      :warning-as-error="warningAsError"
      @toggle="handleToggleMenu"
      @change="handleTemplateChange"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import orderBy from "lodash/orderBy";

import {
  PLACEMENT_NO_DESTINATION_OFFER_TEMPLATE,
  PLACEMENT_NO_ORIGIN_OFFER_TEMPLATE,
  PLACEMENT_NO_WFB_TEMPLATE,
} from "@/helpers/constants/events";
import { getMissingOfferTemplates } from "@/helpers/offers";
import { usePromiseWrapper } from "@/hooks/common";
import { useEmitter } from "@/hooks/common";
import fundersApiService from "@/services/modules/funders";
import offerTemplateService from "@/services/modules/offerTemplate";

import { type DropdownTeleportOptions } from "@/components/ui/inputs/LfDropdown.vue";

import type { IFunder } from "@/models/funders";
import type { ProductType } from "@/models/options";

interface Option {
  label: string;
  value: string | number;
}

const ERROR_DURATION = 3000 as const;

const props = defineProps<{
  value?: string | null;
  model?: (IFunder & { funder_id?: string }) | null;
  placeholder?: string;
  teleportOptions?: DropdownTeleportOptions;
  disabled?: boolean;
  linkedWfbTemplateId?: string | null;
  asBadge?: boolean;
  showPlaceholder?: boolean;
  fullWidth?: boolean;
  noneOption?: Option;
}>();

const INITIAL_OPTIONS = props.noneOption
  ? { [props.noneOption.value]: props.noneOption.label }
  : {};

const emit = defineEmits<{
  change: [funderId: string | undefined, value: string];
}>();

const emitter = useEmitter();

const funderProductTypes = ref<ProductType[]>([]);

const dropdownWrapper = ref<HTMLDivElement | null>(null);

const options = ref<Record<string, string>>({ ...INITIAL_OPTIONS });
const warningAsError = ref(false);

const funderId = computed(() => props.model?.funder_id ?? props.model?.id);

const { fetchWrapper: getOfferTemplates, loading: isOfferTemplatesLoading } =
  usePromiseWrapper(async (workflowTemplateId: string) => {
    const offerTemplatesResponse = await offerTemplateService.getTemplates({
      enabled: 1,
      workflow_template: workflowTemplateId,
      funders: funderId.value ? [funderId.value] : undefined,
    });
    const customOfferTemplatesResponse = offerTemplatesResponse.data.filter(
      (template) => !template.is_fallback
    );
    options.value = orderBy(
      [
        ...customOfferTemplatesResponse,
        ...getMissingOfferTemplates(customOfferTemplatesResponse, {
          product_types: funderProductTypes.value,
        }),
      ],
      (offerTemplate) => offerTemplate.name.toLowerCase()
    ).reduce(
      (acc: Record<string, string>, offerTemplate) => {
        acc[offerTemplate.id] = offerTemplate.name;
        return acc;
      },
      { ...INITIAL_OPTIONS }
    );
  });

const fundersWfbTemplateId = computed(() => {
  return props.linkedWfbTemplateId ?? props.model?.workflow_template_id;
});

const handleToggleMenu = (isOpen: boolean) => {
  if (fundersWfbTemplateId.value && isOpen) {
    getOfferTemplates(fundersWfbTemplateId.value);
  }
};

const resetOptions = () => {
  options.value = {};
};

const handleTemplateChange = (value: string) => {
  emit("change", props.model?.id, value);
};

emitter.on(
  PLACEMENT_NO_DESTINATION_OFFER_TEMPLATE,
  ({ id, focus, missing }) => {
    const matches = props.model?.id === id;
    if (!matches) {
      return;
    }

    const includesHigherPiorityErrors = missing.some(
      (error) =>
        error === PLACEMENT_NO_WFB_TEMPLATE ||
        error === PLACEMENT_NO_ORIGIN_OFFER_TEMPLATE
    );
    if (focus && !includesHigherPiorityErrors) {
      dropdownWrapper.value?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
    warningAsError.value = true;
    setTimeout(() => {
      warningAsError.value = false;
    }, ERROR_DURATION);
  }
);

onMounted(async () => {
  if (props.value && fundersWfbTemplateId.value) {
    getOfferTemplates(fundersWfbTemplateId.value);
  }
});

watch(
  () => props.model,
  async (newFunder) => {
    if (!newFunder?.product_types && newFunder?.id) {
      const funder = await fundersApiService.getFunder(funderId.value || "");
      funderProductTypes.value = funder.product_types;
    }
    if (newFunder?.product_types) {
      funderProductTypes.value = newFunder.product_types;
    }
    if (props.value && fundersWfbTemplateId.value) {
      getOfferTemplates(fundersWfbTemplateId.value);
    }
  },
  { immediate: true }
);

watch(
  () => props.value,
  () => {
    if (
      props.value &&
      !options.value[props.value] &&
      fundersWfbTemplateId.value
    ) {
      getOfferTemplates(fundersWfbTemplateId.value);
    }
  },
  { immediate: true }
);

defineExpose({
  resetOptions,
});
</script>
